import React, { useCallback, useEffect, useRef, useState } from "react";
import { WebsiteBuilderBanner } from "./banner/WebsiteBuilderBanner";
import { WebsiteBuilderToolbar } from "./toolbar/WebsiteBuilderToolbar";
import { WebsiteBuilderDesignSpace } from "./designSpace/WebsiteBuilderDesignSpace";
import { WebsiteBuilderContext } from "./websiteBuilderContext";
import { TWebsiteBuilderTreeRefMap } from "./hooks/treeRefMap/useTreeRefMapRegistration";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import { WebsiteBuilderDragLayer } from "./dragLayer/WebsiteBuilderDragLayer";
import { useWebsiteBuilderDndManager } from "./hooks/dndManager/useWebsiteBuilderDndManager";
import { useWebsiteBuilderToolbarState } from "./hooks/toolbarState/useWebsiteBuilderToolbarState";
import { useAppDispatch, useAppSelector } from "hooks/redux";
import {
  websiteBuilderExitEditor,
  websiteBuilderLoadGlobalStyles,
  websiteBuilderLoadThemes,
  websiteBuilderRedo,
  websiteBuilderSelectNode,
  websiteBuilderUndo,
} from "redux/actions/websiteBuilder/websiteBuilder";
import { useWebsiteBuilderElementOperations } from "./hooks/elementOperations/useWebsiteBuilderElementOperations";
import { isWebsiteBuilderParentNode } from "./nodes/websiteBuilderNode";
import { useWebsiteBuilderClipboard } from "./hooks/clipboard/useWebsiteBuilderClipboard";
import { useWebsiteBuilderSectionOperations } from "./hooks/sectionOperations/useWebsiteBuilderSectionOperations";
import { WebsiteBuilderPreview } from "./preview/WebsiteBuilderPreview";

export interface IWebsiteBuilderDesignSpaceBoundaries {
  leftX: number;
  rightX: number;
}

export type TWebsiteBuilderScrollContainerRef = React.MutableRefObject<HTMLDivElement>;

export const WEBSITE_BUILDER_PORTAL = "website-builder-portal";

export function WebsiteBuilderEditor() {
  const dispatch = useAppDispatch();

  const selectedNode = useAppSelector(store => store.websiteBuilderStore.selectedNode);
  const treeNodeMap = useAppSelector(store => store.websiteBuilderStore.treeNodeMap);
  const mode = useAppSelector(store => store.websiteBuilderStore.mode);

  const dndManager = useWebsiteBuilderDndManager();
  const { toolbarState, handleSetToolbarMenuType } = useWebsiteBuilderToolbarState();

  const treeRefMap: TWebsiteBuilderTreeRefMap = useRef(new Map());

  const clipboard = useWebsiteBuilderClipboard();
  const elementOperations = useWebsiteBuilderElementOperations(clipboard);
  const sectionOperations = useWebsiteBuilderSectionOperations(clipboard);

  const [designSpaceBoundaries, setDesignSpaceBoundaries] = useState<IWebsiteBuilderDesignSpaceBoundaries>({
    leftX: 0,
    rightX: 0,
  });

  const scrollContainerRef: TWebsiteBuilderScrollContainerRef = useRef(null);

  useEffect(() => {
    dispatch(websiteBuilderLoadThemes());
    dispatch(websiteBuilderLoadGlobalStyles());

    return () => dispatch(websiteBuilderExitEditor());
  }, []);

  const handleKeydown = useCallback(
    (e: KeyboardEvent) => {
      if (mode !== "design" || dndManager.isDragging || (selectedNode != null && selectedNode.editingMode !== "none")) {
        return;
      }

      const modifierKeyPressed = e.ctrlKey || e.metaKey;

      if (e.key === "Escape") {
        handleSetToolbarMenuType("none");

        if (selectedNode != null) {
          dispatch(websiteBuilderSelectNode({ selectedNode: null }));
        }
      } else if (modifierKeyPressed && e.key.toLowerCase() === "z") {
        if (e.shiftKey) {
          dispatch(websiteBuilderRedo());
        } else {
          dispatch(websiteBuilderUndo());
        }
      } else if (selectedNode != null) {
        const node = treeNodeMap.get(selectedNode.id);
        const parentNode = treeNodeMap.get(selectedNode.parentId);

        if (!node || !parentNode || !isWebsiteBuilderParentNode(parentNode)) {
          return;
        }

        if (node.type === "element" && parentNode.type === "section") {
          if (modifierKeyPressed && e.key.toLowerCase() === "c") {
            elementOperations.copyElement(node);
          } else if (modifierKeyPressed && e.key.toLowerCase() === "v") {
            elementOperations.pasteAtElement(node, parentNode);
          } else if (modifierKeyPressed && e.key.toLowerCase() === "d") {
            e.preventDefault();
            elementOperations.duplicateElement(node, parentNode);
          } else if (modifierKeyPressed && e.key === "ArrowUp") {
            e.preventDefault();
            elementOperations.bringToFrontElement(node, parentNode);
          } else if (modifierKeyPressed && e.key === "ArrowDown") {
            e.preventDefault();
            elementOperations.bringToBackElement(node, parentNode);
          } else if (e.key === "Backspace") {
            elementOperations.deleteElement(node, parentNode);
          } else if (modifierKeyPressed && e.key.toLowerCase() === "x") {
            elementOperations.cutElement(node, parentNode);
          }
        }

        if (node.type === "section" && parentNode.type === "root") {
          if (modifierKeyPressed && e.key.toLowerCase() === "c") {
            sectionOperations.copySection(node);
          } else if (modifierKeyPressed && e.key.toLowerCase() === "v") {
            sectionOperations.pasteAtSection(
              node,
              parentNode,
              designSpaceBoundaries.leftX,
              treeRefMap,
              scrollContainerRef,
            );
          } else if (modifierKeyPressed && e.key.toLowerCase() === "d") {
            e.preventDefault();
            sectionOperations.duplicateSection(node, parentNode, treeRefMap);
          } else if (e.key === "Backspace") {
            sectionOperations.deleteSection(node, parentNode);
          } else if (modifierKeyPressed && e.key.toLowerCase() === "x") {
            sectionOperations.cutSection(node, parentNode);
          }
        }
      }
    },
    [
      mode,
      selectedNode?.id,
      selectedNode?.parentId,
      selectedNode?.editingMode,
      dndManager.isDragging,
      treeNodeMap,
      elementOperations,
      sectionOperations,
      designSpaceBoundaries.leftX,
      handleSetToolbarMenuType,
    ],
  );

  useEffect(() => {
    document.addEventListener("keydown", handleKeydown);

    return () => document.removeEventListener("keydown", handleKeydown);
  }, [handleKeydown]);

  return (
    <WebsiteBuilderContext.Provider
      value={{
        treeRefMap,
        elementOperations,
        sectionOperations,
        clipboard,
        designSpaceBoundaries,
        scrollContainerRef,
      }}
    >
      <WebsiteBuilderBanner />
      <div id={WEBSITE_BUILDER_PORTAL}></div>
      {mode === "design" ? (
        <>
          <WebsiteBuilderDesignSpace setDesignSpaceBoundaries={setDesignSpaceBoundaries} />
          <WebsiteBuilderToolbar toolbarState={toolbarState} handleSetToolbarMenuType={handleSetToolbarMenuType} />
          <WebsiteBuilderDragLayer />
        </>
      ) : (
        <WebsiteBuilderPreview />
      )}
    </WebsiteBuilderContext.Provider>
  );
}

export function WebsiteBuilderEditorContainer() {
  return (
    <DndProvider backend={HTML5Backend}>
      <WebsiteBuilderEditor />
    </DndProvider>
  );
}
